import { ArrayParam, BooleanParam, JsonParam, NumberParam, ObjectParam, StringParam } from 'use-query-params'

import { FILTERS_PARAMS_KEYS } from '@app/api/constants'
import { dateFormat } from '@app/constants'
import { ABNORMAL_STATE } from '@app/constants/abnormalState'
import { columnSearchSchemaOptionType } from '@app/hooks/useTableSearch/сonstants'

import type { SortingScheme } from './hooks/useSortingParameters/types'
import type { ProcessingResultsDataSource } from './types'
import type { ColumnTableSearchSchema, TableSearchQuerySchema } from '@app/hooks/useTableSearch/types'
import type { ColumnTableSchema } from '@app/components/ExtendedTable/types'
import type { ColumnTitleSchema } from '@app/types/table'

export const columnTitleMap: ColumnTitleSchema<ProcessingResultsDataSource> = {
  monitoringType: 'Тип мониторинга',
  name: 'Наименование',
  productCode: 'Код товара',
  cluster: 'Кластер',
  store: 'ТК Лента',
  competitor: 'Конкурент',
  purchasePrice: 'Закуп. цена',
  storeRegularPrice: 'Рег. цена Лента',
  regularPriceCompetitor: 'Рег. цена кон-та',
  storePromoPrice: 'Акц. цена Лента',
  promoPriceCompetitor: 'Акц. цена кон-та',
  priceDifference: 'Ценовая разница',
  note: 'Примечание к акции',
  abnormalState: 'Статус обработки',
  monitoringInstruction: 'Как мониторить',
  date: 'Дата мониторинга',
  promotionName: 'Рекламное мероприятие',
  promoDateFrom: 'Дата начала РМ Лента',
  promoDateTo: 'Дата оконч. РМ Лента',

  // TODO: добавить потом табличные фильтры для этих колонок
  startPromoDateAt: 'Дата начала промо',
  endPromoDateAt: 'Дата оконч. промо',
  discount: 'Скидка кон-та',
  discountMechanics: 'Механика скидки',
  productRating: 'Рейтинг товара'
}

export const columnSettingSchema: ColumnTableSchema<ProcessingResultsDataSource> = {
  name: { customEllipsis: true },
  note: { customEllipsis: true },
  monitoringInstruction: { customEllipsis: true }
}

export const topFiltersQuerySchema = {
  [FILTERS_PARAMS_KEYS.monitoringKindIds]: ArrayParam, // Вид мониторинга
  [FILTERS_PARAMS_KEYS.week]: StringParam, // Неделя
  [FILTERS_PARAMS_KEYS.collectedDateAtAfter]: StringParam, // Дата от
  [FILTERS_PARAMS_KEYS.collectedDateAtBefore]: StringParam, // Дата до
  [FILTERS_PARAMS_KEYS.monitoringTypeIds]: ArrayParam, // Тип мониторинга
  [FILTERS_PARAMS_KEYS.storeIds]: ArrayParam, // ТК Лента
  [FILTERS_PARAMS_KEYS.clusterIds]: ArrayParam, // Кластер
  [FILTERS_PARAMS_KEYS.salesOrganizationIds]: ArrayParam, // Сбытовая организация
  [FILTERS_PARAMS_KEYS.competitorIds]: ArrayParam, // Сеть конкурента
  [FILTERS_PARAMS_KEYS.competitorNetworkIds]: ArrayParam, // Адрес конкурента
  [FILTERS_PARAMS_KEYS.priceType]: StringParam, // Все цены
  [FILTERS_PARAMS_KEYS.priceDifferenceFrom]: StringParam, // Все цены от, % - string
  [FILTERS_PARAMS_KEYS.priceDifferenceTo]: StringParam, // Все цены до, % - string
  [FILTERS_PARAMS_KEYS.excludeRangePriceDifference]: BooleanParam, // Вне диапазона
  [FILTERS_PARAMS_KEYS.abnormalState]: ArrayParam, // Статус обработки
  [FILTERS_PARAMS_KEYS.productCodes]: StringParam, // Код товара
  [FILTERS_PARAMS_KEYS.productName]: StringParam, // Название товара
  [FILTERS_PARAMS_KEYS.purchasingGroupIds]: ArrayParam, // Группа закупок
  [FILTERS_PARAMS_KEYS.trendIds]: ArrayParam, // Группа закупок
  [FILTERS_PARAMS_KEYS.commodityGroupIds]: ArrayParam, // Товарная группа
  [FILTERS_PARAMS_KEYS.isMatchingStatus]: StringParam, // Статус сопоставления
  [FILTERS_PARAMS_KEYS.commentsByUsers]: ArrayParam, // Комментарии - select
  [FILTERS_PARAMS_KEYS.isCHTM]: BooleanParam, // ЧТМ - checkbox
  [FILTERS_PARAMS_KEYS.isEPP]: BooleanParam, // ЕРР - checkbox
  [FILTERS_PARAMS_KEYS.isInfo]: BooleanParam, // Info - checkbox
  [FILTERS_PARAMS_KEYS.isShortKvi]: BooleanParam, // shortKvi - checkbox
  [FILTERS_PARAMS_KEYS.isTop1]: BooleanParam, // Top 1 - checkbox
  tempMockBigMedia: BooleanParam, // Big Media - checkbox
  [FILTERS_PARAMS_KEYS.startPromoDateAt]: ArrayParam, // Дата начала промо
  [FILTERS_PARAMS_KEYS.endPromoDateAt]: ArrayParam, // Дата окончания промо
  [FILTERS_PARAMS_KEYS.maxDiscount]: ArrayParam, // Скидка конкурента от %:
  [FILTERS_PARAMS_KEYS.minDiscount]: StringParam, // Скидка конкурента до %:
  [FILTERS_PARAMS_KEYS.promoIds]: ArrayParam // Рекламное мероприятие
}

// TODO закомментированы цены так как это приходит как объект дальше в хук. Необходимо доработать сортировку
export const tableSearchQuerySchema: TableSearchQuerySchema<ProcessingResultsDataSource> = {
  'table-monitoringType': StringParam,
  'table-name': StringParam,
  'table-productCode': StringParam,
  'table-cluster': StringParam,
  'table-store': StringParam,
  'table-competitor': StringParam,
  'table-purchasePrice': NumberParam,
  'table-storeRegularPrice': NumberParam,
  // 'table-regularPriceCompetitor': NumberParam,
  'table-storePromoPrice': NumberParam,
  // 'table-promoPriceCompetitor': NumberParam,
  'table-priceDifference': NumberParam,
  'table-note': StringParam,
  'table-abnormalState': JsonParam,
  'table-monitoringInstruction': StringParam,
  'table-date': ObjectParam,
  'table-promotionName': StringParam,
  'table-promoDateFrom': ObjectParam,
  'table-promoDateTo': ObjectParam
} as const

const titleWrapperProps = { minWidth: 200 } as const
const titleWrapperDateProps = { minWidth: 215 } as const

const abnormalStateOptions = [
  { label: 'Непроверенный', value: ABNORMAL_STATE.OK },
  { label: 'Аномальный', value: ABNORMAL_STATE.ABNORMAL },
  { label: 'Верный', value: ABNORMAL_STATE.CHECKED }
]

export const columnsSearchSchema: ColumnTableSearchSchema<ProcessingResultsDataSource> = {
  monitoringType: { type: columnSearchSchemaOptionType.INPUT, titleWrapperProps },
  name: { type: columnSearchSchemaOptionType.INPUT, titleWrapperProps },
  productCode: { type: columnSearchSchemaOptionType.INPUT, titleWrapperProps },
  cluster: { type: columnSearchSchemaOptionType.INPUT, titleWrapperProps },
  store: { type: columnSearchSchemaOptionType.INPUT, titleWrapperProps },
  competitor: { type: columnSearchSchemaOptionType.INPUT, titleWrapperProps },
  purchasePrice: { type: columnSearchSchemaOptionType.NUMERIC_INPUT, titleWrapperProps },
  storeRegularPrice: { type: columnSearchSchemaOptionType.NUMERIC_INPUT, titleWrapperProps },
  // regularPriceCompetitor: { type: columnSearchSchemaOptionType.NUMERIC_INPUT, titleWrapperProps },
  storePromoPrice: { type: columnSearchSchemaOptionType.NUMERIC_INPUT, titleWrapperProps },
  // promoPriceCompetitor: { type: columnSearchSchemaOptionType.NUMERIC_INPUT, titleWrapperProps },
  priceDifference: { type: columnSearchSchemaOptionType.NUMERIC_INPUT, titleWrapperProps },
  note: { type: columnSearchSchemaOptionType.INPUT, titleWrapperProps },
  abnormalState: {
    type: columnSearchSchemaOptionType.SELECT,
    titleWrapperProps,
    controlProps: { options: abnormalStateOptions }
  },
  monitoringInstruction: { type: columnSearchSchemaOptionType.INPUT, titleWrapperProps },
  date: {
    type: columnSearchSchemaOptionType.DATE_RANGE,
    titleWrapperProps: titleWrapperDateProps,
    controlProps: { format: dateFormat.DD_MM_YYYY }
  },
  promotionName: { type: columnSearchSchemaOptionType.INPUT, titleWrapperProps },
  promoDateFrom: {
    type: columnSearchSchemaOptionType.DATE_RANGE,
    titleWrapperProps: titleWrapperDateProps,
    controlProps: { format: dateFormat.DD_MM_YYYY }
  },
  promoDateTo: {
    type: columnSearchSchemaOptionType.DATE_RANGE,
    titleWrapperProps: titleWrapperDateProps,
    controlProps: { format: dateFormat.DD_MM_YYYY }
  }
}

export const sortingScheme: SortingScheme<ProcessingResultsDataSource> = {
  monitoringType: 'monitoringType',
  productCode: 'productCode',
  cluster: 'cluster',
  competitor: 'competitorAddress',
  regularPriceCompetitor: 'regularPrice',
  promoPriceCompetitor: 'promoPrice',
  priceDifference: 'displayPriceDifference',
  date: 'monitoringStartAt'
}
